import React, { useState, useEffect, useMemo } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Search, RefreshCw, Download, Loader2, AlertCircle, ArrowUp, ArrowDown, ArrowUpDown,
  ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ReportPeriodFilter from './ReportPeriodFilter';
import {
  ALL, NO_JOB, NO_STAGE, JOB_STATUS_COLORS, RECOMMENDATION_COLORS, STICKY_COL, jobStatusRank, formatDate, daysSince,
  fileSafe, downloadWorkbook, hasPeriod, periodBounds, periodPhrase, periodFileTag
} from './reportUtils';

const PAGE_SIZE = 50;

// What a period selects in the candidate report: candidates added in it by
// default, or those who moved stage in it (as Passed through counts them).
const BASIS_OPTIONS = [
  { value: 'added', label: 'Added in period' },
  { value: 'moved', label: 'Moved stage in period' }
];

// Display order for statuses — the list GET /recruitment/statuses returns.
const CANDIDATE_STATUSES = [
  'New', 'In Progress', 'On Hold', 'Rejected', 'Offer Extended',
  'Offer Accepted', 'Offer Rejected', 'Joined', 'Converted', 'Withdrawn'
];

// Same colours as the Candidates tab.
const STATUS_COLORS = {
  'New': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'On Hold': 'bg-gray-100 text-gray-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Offer Extended': 'bg-purple-100 text-purple-700',
  'Offer Accepted': 'bg-green-100 text-green-700',
  'Offer Rejected': 'bg-red-100 text-red-700',
  'Joined': 'bg-emerald-100 text-emerald-700',
  'Converted': 'bg-teal-100 text-teal-700',
  'Withdrawn': 'bg-slate-100 text-slate-700'
};

const OFFER_COLORS = {
  'Pending': 'bg-amber-100 text-amber-700',
  'Accepted': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Expired': 'bg-slate-100 text-slate-700'
};

const stageKey = (stage) => stage || NO_STAGE;
const stageLabel = (key) => (key === NO_STAGE ? 'No stage' : key);

const statusRank = (status) => {
  const i = CANDIDATE_STATUSES.indexOf(status);
  return i === -1 ? CANDIDATE_STATUSES.length : i;
};

const isEmpty = (v) => v === null || v === undefined || v === '';

// Empty values sort last whichever way the column is sorted.
const compare = (a, b, dir = 'asc') => {
  if (isEmpty(a) || isEmpty(b)) {
    if (isEmpty(a) && isEmpty(b)) return 0;
    return isEmpty(a) ? 1 : -1;
  }
  const order = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return dir === 'asc' ? order : -order;
};

// Sort key -> [value, direction of the first click]
const SORTS = {
  name: [(c) => c.name, 'asc'],
  job: [(c) => c.job_title, 'asc'],
  stage: [(c) => c.stage_order, 'asc'],
  days: [(c) => c.days_in_stage, 'desc'],
  status: [(c) => statusRank(c.status), 'asc'],
  experience: [(c) => c.years_of_experience, 'desc'],
  added: [(c) => c.created_at, 'desc']
};

// Across all jobs: by job, then pipeline order. Otherwise: pipeline order.
const defaultSort = (allJobs) => ({ key: allJobs ? 'job' : 'stage', dir: 'asc', allJobs });

const sortRows = (rows, { key, dir }) => {
  const [value] = SORTS[key];
  return [...rows].sort((a, b) => (
    compare(value(a), value(b), dir)
    || (key === 'job' ? compare(a.stage_order, b.stage_order) : compare(a.job_title, b.job_title))
    || compare(a.current_stage, b.current_stage)
    || compare(a.name, b.name)
  ));
};

const lpa = (value) => (isEmpty(value) ? null : Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

const daysLabel = (days) => {
  if (days === null || days === undefined) return null;
  if (days === 0) return 'Today';
  return days === 1 ? '1 day' : `${days} days`;
};

const matchesSearch = (c, q) => !q || [
  c.name, c.email, c.phone, c.current_company, c.location, c.source, c.referred_by, ...c.skills, ...c.tags
].some((field) => field && String(field).toLowerCase().includes(q));

// Excel columns: [header, value, width, onlyWhenOffersExist]
const EXPORT_COLUMNS = [
  ['Candidate', (c) => c.name, 24],
  ['Email', (c) => c.email, 28],
  ['Phone', (c) => c.phone, 16],
  ['Location', (c) => c.location, 14],
  ['Current Company', (c) => c.current_company, 20],
  ['Job', (c) => c.job_title || 'Not linked to a job', 26],
  ['Department', (c) => c.job_department, 16],
  ['Stage', (c) => stageLabel(c.stage_key), 18],
  ['In Stage Since', (c) => formatDate(c.stage_entered_at, 'yyyy-MM-dd'), 14],
  ['Days in Stage', (c) => c.days_in_stage, 13],
  ['Stages Passed Through', (c) => (c.stages_reached || []).join(' → '), 44],
  ['Status', (c) => c.status, 15],
  ['Experience (yrs)', (c) => c.years_of_experience, 15],
  ['Current CTC (LPA)', (c) => c.current_ctc, 17],
  ['Expected CTC (LPA)', (c) => c.expected_ctc, 18],
  ['Notice Period', (c) => c.notice_period, 13],
  ['Source', (c) => c.source, 16],
  ['Referred By', (c) => c.referred_by, 24],
  ['Skills', (c) => c.skills.join(', '), 32],
  ['Tags', (c) => c.tags.join(', '), 20],
  ['Interviews', (c) => c.interviews_total, 11],
  ['Interviews Completed', (c) => c.interviews_completed, 20],
  ['Interview Rounds', (c) => (c.interviews || []).map(interviewForExcel).join('; '), 60],
  ['Last Interview', (c) => c.last_interview?.round_name, 18],
  ['Last Interview Date', (c) => formatDate(c.last_interview?.scheduled_at, 'yyyy-MM-dd'), 18],
  ['Last Recommendation', (c) => c.last_interview?.recommendation, 20],
  ['Next Interview', (c) => c.next_interview?.round_name, 18],
  ['Next Interview On', (c) => formatDate(c.next_interview?.scheduled_at, 'yyyy-MM-dd HH:mm'), 18],
  ['Offer Status', (c) => c.offer?.status, 13, true],
  ['Offered CTC', (c) => c.offer?.offered_ctc, 12, true],
  ['Joining Date', (c) => formatDate(c.offer?.joining_date, 'yyyy-MM-dd'), 13, true],
  ['Resume', (c) => (c.has_resume ? 'Yes' : 'No'), 9],
  ['Added On', (c) => formatDate(c.created_at, 'yyyy-MM-dd'), 12],
  ['Added By', (c) => c.created_by, 16],
  ['Last Updated', (c) => formatDate(c.updated_at, 'yyyy-MM-dd'), 13]
];

const Dash = () => <span className="text-slate-300">—</span>;

const SortHeader = ({ label, title, sortKey, sort, onSort, className = '' }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead
      className={`whitespace-nowrap ${className}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={title ? `Sort by ${title.toLowerCase()}` : undefined}
        aria-label={title}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? 'text-slate-900' : ''}`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? '' : 'text-slate-300'}`} aria-hidden="true" />
      </button>
    </TableHead>
  );
};

const hasTime = (value) => /T\d{2}:\d{2}/.test(value || '');

const interviewDate = (interview) => (
  interview.scheduled_at ? formatDate(interview.scheduled_at) || interview.scheduled_at : 'No date'
);

// How a round went: the verdict once completed, otherwise its status. A
// scheduled round shows its time here, keeping the date column narrow.
const interviewOutcome = (interview) => {
  const { status, recommendation, scheduled_at: when } = interview;
  if (status === 'Completed') {
    return recommendation
      ? { text: recommendation, className: RECOMMENDATION_COLORS[recommendation] || 'text-slate-700' }
      : { text: 'completed', className: 'text-muted-foreground' };
  }
  if (status === 'Scheduled') {
    return { text: hasTime(when) ? `scheduled, ${formatDate(when, 'h:mm a')}` : 'scheduled', className: 'text-blue-700' };
  }
  if (status === 'In Progress') return { text: 'in progress', className: 'text-blue-700' };
  if (status === 'No Show') return { text: 'no show', className: 'text-amber-700' };
  return { text: (status || '').toLowerCase(), className: 'text-muted-foreground' };
};

const describeInterview = (interview) => (
  [interviewDate(interview), interview.round_name, interviewOutcome(interview).text].filter(Boolean).join(' · ')
);

// Excel: sortable date and time, and the outcome as a word.
const excelWhen = (interview) => (
  interview.scheduled_at
    ? formatDate(interview.scheduled_at, hasTime(interview.scheduled_at) ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd')
    : 'no date'
);
const excelOutcome = (interview) => (
  interview.status === 'Completed' ? (interview.recommendation || 'Completed') : (interview.status || '')
);
const interviewForExcel = (interview) => `${excelWhen(interview)} ${interview.round_name} (${excelOutcome(interview)})`;

// One row per interview of the exported candidates, for sorting and
// filtering by date in Excel.
const INTERVIEW_SHEET_COLUMNS = [
  ['Candidate', (c) => c.name, 24],
  ['Email', (c) => c.email, 28],
  ['Job', (c) => c.job_title || 'Not linked to a job', 26],
  ['Current Stage', (c) => stageLabel(c.stage_key), 18],
  ['Round No.', (c, i) => i.round_number, 10],
  ['Round', (c, i) => i.round_name, 20],
  ['Date', (c, i) => (i.scheduled_at ? formatDate(i.scheduled_at, 'yyyy-MM-dd') : ''), 12],
  ['Time', (c, i) => (hasTime(i.scheduled_at) ? formatDate(i.scheduled_at, 'HH:mm') : ''), 8],
  ['Status', (c, i) => i.status, 12],
  ['Recommendation', (c, i) => i.recommendation, 16],
  ['Interviewer', (c, i) => i.interviewer_name, 20]
];

const ROUNDS_SHOWN = 4;

// Every round, oldest first, in date | round | outcome columns. A long
// history shows its latest rounds; the earlier ones are a click away.
const InterviewList = ({ interviews }) => {
  const [expanded, setExpanded] = useState(false);
  if (!interviews?.length) return <Dash />;
  const first = expanded ? 0 : Math.max(interviews.length - ROUNDS_SHOWN, 0);
  return (
    <ul className="grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-1 text-xs">
      {first > 0 && (
        <li className="col-span-full">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            title={interviews.slice(0, first).map(describeInterview).join('\n')}
            className="text-muted-foreground hover:text-slate-800 hover:underline"
          >
            +{first} earlier {first === 1 ? 'round' : 'rounds'}
          </button>
        </li>
      )}
      {interviews.slice(first).map((interview, n) => {
        const outcome = interviewOutcome(interview);
        return (
          <li
            key={first + n}
            className="col-span-full grid grid-cols-subgrid whitespace-nowrap"
            title={interview.interviewer_name ? `Interviewer: ${interview.interviewer_name}` : undefined}
          >
            <span className="tabular-nums text-slate-700">{interviewDate(interview)}</span>
            <span className="font-medium text-slate-800">{interview.round_name}</span>
            <span className={outcome.className}>{outcome.text}</span>
          </li>
        );
      })}
    </ul>
  );
};

// The detailed report: every candidate for the chosen job (or all jobs, or
// none) with the details HR asks about. The job, current-stage and
// passed-through filters and the period live in the URL, owned by the
// parent; status, search, sort and page are local.
const CandidateReport = ({
  jobFilter, stageFilter, reachedFilter = ALL, onFiltersChange, getCandidateUrl,
  period = { from: ALL, to: ALL }, basis = ALL, onPeriodChange, onBasisChange
}) => {
  const { jobs, getCandidateReport } = useRecruitment();
  const { from, to } = period;
  const periodSet = hasPeriod(from, to);
  const listBasis = periodSet && basis === 'moved' ? 'moved' : 'added';
  // Candidates who moved stage in the period, rather than those added in it.
  const movedList = periodSet && listBasis === 'moved';
  // Everything the server is asked for; a loaded report answers exactly one.
  const requestKey = [jobFilter, from, to, periodSet ? listBasis : ''].join('|');

  // The loaded report and the last failure are each tagged with the request
  // they answer, so after the job or period changes neither is shown under
  // the new one — not even for the frame before the next request starts.
  const [report, setReport] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [search, setSearch] = useState('');

  const singleJob = jobFilter !== ALL && jobFilter !== NO_JOB;
  // Only the all-jobs report mixes jobs, so only it has a Job column.
  const allJobs = jobFilter === ALL;

  // A header click picks the sort for the current kind of report; moving to
  // or from the all-jobs report goes back to that kind's default.
  const [chosenSort, setChosenSort] = useState(() => defaultSort(allJobs));
  const sort = useMemo(
    () => (chosenSort.allJobs === allJobs ? chosenSort : defaultSort(allJobs)),
    [chosenSort, allJobs]
  );

  useEffect(() => {
    let cancelled = false;
    const key = [jobFilter, from, to, periodSet ? listBasis : ''].join('|');
    setLoading(true);
    getCandidateReport(
      jobFilter === ALL ? null : jobFilter,
      { ...periodBounds(from, to), basis: periodSet ? listBasis : null }
    ).then((result) => {
      if (cancelled) return;
      if (result.success) {
        setReport({ ...result.data, forKey: key });
        setFailure(null);
      } else {
        setFailure({ message: result.error, forKey: key });
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobFilter, from, to, periodSet, listBasis, reloadKey, getCandidateReport]);

  const current = report?.forKey === requestKey ? report : null;
  const error = failure?.forKey === requestKey ? failure.message : null;

  const rows = useMemo(() => (current ? current.candidates.map((c) => ({
    ...c,
    stage_key: stageKey(c.current_stage),
    days_in_stage: daysSince(c.stage_entered_at)
  })) : []), [current]);

  const q = search.trim().toLowerCase();
  // Search and "passed through" narrow the list before the stage and status
  // counts are taken.
  const scoped = useMemo(() => rows.filter((c) => (
    matchesSearch(c, q)
    // With "moved stage in period", passed through means moved into it then.
    && (reachedFilter === ALL || ((movedList ? c.stages_moved : c.stages_reached) || []).includes(reachedFilter))
  )), [rows, q, reachedFilter, movedList]);

  // Each filter's counts reflect the other filters but not itself.
  const stageCounts = useMemo(() => {
    const counts = {};
    scoped.forEach((c) => {
      if (statusFilter === ALL || c.status === statusFilter) counts[c.stage_key] = (counts[c.stage_key] || 0) + 1;
    });
    return counts;
  }, [scoped, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = {};
    scoped.forEach((c) => {
      if (stageFilter === ALL || c.stage_key === stageFilter) counts[c.status] = (counts[c.status] || 0) + 1;
    });
    return counts;
  }, [scoped, stageFilter]);

  const filtered = useMemo(() => scoped.filter((c) => (
    (stageFilter === ALL || c.stage_key === stageFilter)
    && (statusFilter === ALL || c.status === statusFilter)
  )), [scoped, stageFilter, statusFilter]);

  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  // Back to page 1 whenever what is listed changes.
  const listSignature = [requestKey, stageFilter, reachedFilter, statusFilter, q, sort.key, sort.dir].join('\u0000');
  const [pageState, setPageState] = useState({ signature: listSignature, page: 1 });
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const page = pageState.signature === listSignature ? Math.min(pageState.page, pageCount) : 1;
  const setPage = (next) => setPageState({ signature: listSignature, page: next });
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stage chips: the job's whole pipeline (zeros included) for one job, then
  // any other stage present, in pipeline order.
  const stageChips = useMemo(() => {
    const keys = [];
    const add = (key) => { if (!keys.includes(key)) keys.push(key); };
    (current?.pipeline?.stages || []).forEach(add);
    [...rows]
      .sort((a, b) => compare(a.stage_order, b.stage_order) || compare(a.current_stage, b.current_stage))
      .forEach((c) => add(c.stage_key));
    if (stageFilter !== ALL) add(stageFilter);
    return keys;
  }, [current, rows, stageFilter]);

  const statusOptions = useMemo(() => {
    const present = [...new Set(rows.map((c) => c.status).filter(Boolean))];
    if (statusFilter !== ALL && !present.includes(statusFilter)) present.push(statusFilter);
    return present.sort((a, b) => statusRank(a) - statusRank(b) || a.localeCompare(b));
  }, [rows, statusFilter]);

  const jobOptions = useMemo(() => {
    const list = [...jobs];
    // The job named in a shared link may not be in the jobs list yet.
    if (singleJob && current?.job && !list.some((j) => j.id === current.job.id)) list.push(current.job);
    return list.sort((a, b) => jobStatusRank(a.status) - jobStatusRank(b.status) || (a.title || '').localeCompare(b.title || ''));
  }, [jobs, singleJob, current]);

  const hasOffers = rows.some((c) => c.offer);
  const filtersActive = stageFilter !== ALL || reachedFilter !== ALL || statusFilter !== ALL || q !== '';
  const totalForChips = Object.values(stageCounts).reduce((sum, n) => sum + n, 0);

  const handleSort = (key) => {
    setChosenSort(sort.key === key
      ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc', allJobs }
      : { key, dir: SORTS[key][1], allJobs });
  };

  const setStage = (key) => onFiltersChange({ job: jobFilter, stage: key, reached: reachedFilter });
  const clearReached = () => onFiltersChange({ job: jobFilter, stage: stageFilter, reached: ALL });

  const clearFilters = () => {
    setStatusFilter(ALL);
    setSearch('');
    if (stageFilter !== ALL || reachedFilter !== ALL) onFiltersChange({ job: jobFilter, stage: ALL, reached: ALL });
  };

  const scopeTitle = singleJob
    ? (current?.job?.title || jobOptions.find((j) => j.id === jobFilter)?.title || 'Selected job')
    : jobFilter === NO_JOB ? 'Not linked to a job' : 'All jobs';

  const scopeDetail = singleJob
    ? [current?.job?.department, current?.job?.location, current?.pipeline && `${current.pipeline.name} pipeline`].filter(Boolean).join(' · ')
    : jobFilter === NO_JOB
      ? 'Candidates without a job, or whose job no longer exists'
      : 'Every candidate, across all jobs';

  const phrase = periodPhrase(from, to);
  const periodDescription = !periodSet ? null
    : movedList ? `Candidates who moved into a stage ${phrase}.`
      : `Candidates added ${phrase}.`;

  const handleExport = () => {
    if (sorted.length === 0) {
      toast.error('There is nothing to export');
      return;
    }
    const columns = [
      ...EXPORT_COLUMNS.filter(([, , , offerOnly]) => hasOffers || !offerOnly),
      ...(movedList ? [['Moved Into (In Period)', (c) => (c.stages_moved || []).join(' → '), 30]] : [])
    ];
    const sheetRows = [
      columns.map(([header]) => header),
      ...sorted.map((c) => columns.map(([, value]) => value(c) ?? ''))
    ];
    const scope = [
      scopeTitle,
      reachedFilter !== ALL && `passed ${reachedFilter}`,
      stageFilter !== ALL && stageLabel(stageFilter),
      periodSet && (movedList ? 'moved' : 'added'),
      periodFileTag(from, to)
    ].filter(Boolean).join(' ');
    const interviewRows = sorted.flatMap((c) => (c.interviews || []).map((i) => (
      INTERVIEW_SHEET_COLUMNS.map(([, value]) => value(c, i) ?? '')
    )));
    downloadWorkbook(
      [
        { name: 'Candidates', rows: sheetRows, widths: columns.map(([, , width]) => width), filter: true, note: periodDescription },
        ...(interviewRows.length > 0 ? [{
          name: 'Interviews',
          rows: [INTERVIEW_SHEET_COLUMNS.map(([header]) => header), ...interviewRows],
          widths: INTERVIEW_SHEET_COLUMNS.map(([, , width]) => width),
          filter: true,
          note: periodDescription
        }] : [])
      ],
      `Candidate_Report_${fileSafe(scope)}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
    );
    toast.success(`Exported ${sorted.length} ${sorted.length === 1 ? 'candidate' : 'candidates'}`);
  };

  const columnCount = 11 + (allJobs ? 1 : 0) + (hasOffers ? 1 : 0);

  return (
    <div className="flex flex-col gap-6" data-testid="candidate-report">
      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-4">
          <ReportPeriodFilter
            from={from}
            to={to}
            onChange={onPeriodChange}
            basis={listBasis}
            basisOptions={BASIS_OPTIONS}
            onBasisChange={onBasisChange}
            description={periodDescription}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select value={jobFilter} onValueChange={(job) => onFiltersChange({ job, stage: ALL })}>
              <SelectTrigger className="w-full sm:w-[280px]" aria-label="Job" data-testid="candidate-report-job-filter">
                <SelectValue placeholder="Select a job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All jobs</SelectItem>
                <SelectItem value={NO_JOB}>Not linked to a job</SelectItem>
                {jobOptions.length > 0 && <SelectSeparator />}
                {jobOptions.map((job) => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.title}{job.status && job.status !== 'Open' ? ` (${job.status})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[190px]" aria-label="Candidate status" data-testid="candidate-report-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status} ({statusCounts[status] || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search name, email, phone, company, skills..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                aria-label="Search candidates"
                data-testid="candidate-report-search"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="w-4 h-4" /> Clear
                </Button>
              )}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setReloadKey((k) => k + 1)}
                disabled={loading}
                aria-label="Refresh report"
                data-testid="refresh-candidate-report"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={!current || sorted.length === 0} data-testid="export-candidate-report">
                <Download className="w-4 h-4 mr-2" /> Export to Excel
              </Button>
            </div>
          </div>

          {reachedFilter !== ALL && (
            <div className="flex flex-wrap items-center gap-2" data-testid="candidate-report-reached-filter">
              <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 py-0.5 pl-3 pr-1 text-xs font-medium text-purple-800">
                Passed through: {stageLabel(reachedFilter)}
                <button
                  type="button"
                  onClick={clearReached}
                  className="rounded-full p-0.5 hover:bg-purple-100"
                  aria-label={`Remove the "passed through ${stageLabel(reachedFilter)}" filter`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              <span className="text-xs text-muted-foreground">
                {movedList ? `Candidates who moved into this stage ${phrase}` : 'Candidates who have been at this stage at any point'}
              </span>
            </div>
          )}

          {current && rows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by current stage">
              <span className="text-xs font-medium text-muted-foreground mr-1">Current stage</span>
              <button
                type="button"
                onClick={() => setStage(ALL)}
                aria-pressed={stageFilter === ALL}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  stageFilter === ALL ? 'border-purple-600 bg-purple-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                All <span className="tabular-nums opacity-80">{totalForChips}</span>
              </button>
              {stageChips.map((key) => {
                const count = stageCounts[key] || 0;
                const active = stageFilter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStage(active ? ALL : key)}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      active
                        ? 'border-purple-600 bg-purple-600 text-white'
                        : count > 0
                          ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          : 'border-dashed border-slate-200 bg-white text-slate-400 hover:bg-slate-50'
                    }`}
                    data-testid={`stage-chip-${key}`}
                  >
                    {stageLabel(key)} <span className="tabular-nums opacity-80">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {error && current && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Couldn't refresh the report: {error}. Showing the last loaded data.
        </div>
      )}

      {/* Candidates */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-lg">{scopeTitle}</CardTitle>
                {current?.job?.status && (
                  <Badge className={`${JOB_STATUS_COLORS[current.job.status] || 'bg-slate-100 text-slate-700'} font-normal`}>
                    {current.job.status}
                  </Badge>
                )}
              </div>
              {scopeDetail && <p className="text-sm text-muted-foreground mt-1">{scopeDetail}</p>}
            </div>
            {current && (
              <Badge variant="secondary" className="font-normal" data-testid="candidate-report-count">
                {filtersActive
                  ? `${filtered.length} of ${rows.length} candidates`
                  : `${rows.length} ${rows.length === 1 ? 'candidate' : 'candidates'}`}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!current ? (
            !error ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center gap-3 text-center">
                <AlertCircle className="w-10 h-10 text-red-400" />
                <p className="text-slate-700 font-medium">Couldn't load the report</p>
                <p className="text-sm text-muted-foreground">{error}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Try again
                  </Button>
                  {jobFilter !== ALL && (
                    <Button variant="outline" onClick={() => onFiltersChange({ job: ALL, stage: ALL })}>
                      Show all jobs
                    </Button>
                  )}
                </div>
              </div>
            )
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <SortHeader label="Candidate" sortKey="name" sort={sort} onSort={handleSort} className={`${STICKY_COL} bg-white min-w-[130px] sm:min-w-[170px]`} />
                      <TableHead className="min-w-[170px]">Contact</TableHead>
                      {allJobs && <SortHeader label="Job" sortKey="job" sort={sort} onSort={handleSort} className="min-w-[160px]" />}
                      <SortHeader label="Stage" sortKey="stage" sort={sort} onSort={handleSort} className="min-w-[120px]" />
                      <SortHeader label="In stage" sortKey="days" sort={sort} onSort={handleSort} />
                      <SortHeader label="Status" sortKey="status" sort={sort} onSort={handleSort} />
                      <SortHeader label="Exp." title="Experience" sortKey="experience" sort={sort} onSort={handleSort} />
                      <TableHead className="whitespace-nowrap">CTC (LPA)</TableHead>
                      <TableHead className="whitespace-nowrap">Notice</TableHead>
                      <TableHead className="min-w-[110px]">Source</TableHead>
                      <TableHead>Interviews</TableHead>
                      {hasOffers && <TableHead>Offer</TableHead>}
                      <SortHeader label="Added" sortKey="added" sort={sort} onSort={handleSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.length === 0 ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columnCount} className="py-12 text-center text-muted-foreground">
                          {rows.length === 0 ? (
                            periodSet
                              ? (movedList ? 'Nobody moved stage in this period.' : 'No candidates were added in this period.')
                              : (jobFilter === NO_JOB ? 'Every candidate is linked to a job.' : 'No candidates yet.')
                          ) : (
                            <span className="inline-flex flex-col items-center gap-2">
                              No candidates match these filters.
                              <Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : pageRows.map((c) => (
                      <TableRow key={c.id} className="group align-top hover:bg-slate-50" data-testid={`candidate-report-row-${c.id}`}>
                        <TableCell className={`${STICKY_COL} bg-white group-hover:bg-slate-50`}>
                          <a
                            href={getCandidateUrl(c.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-slate-900 hover:text-purple-600 hover:underline"
                            title="Open candidate in a new tab"
                          >
                            {c.name}
                          </a>
                          {c.current_company && <p className="text-xs text-muted-foreground">{c.current_company}</p>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <p className="max-w-[200px] truncate" title={c.email}>{c.email}</p>
                          {c.phone && <p className="text-muted-foreground whitespace-nowrap">{c.phone}</p>}
                        </TableCell>
                        {allJobs && (
                          <TableCell className="text-sm">
                            {c.job_title ? (
                              <>
                                <p>{c.job_title}</p>
                                {c.job_department && <p className="text-xs text-muted-foreground">{c.job_department}</p>}
                              </>
                            ) : <span className="text-muted-foreground">Not linked</span>}
                          </TableCell>
                        )}
                        <TableCell className="text-sm">
                          <p className={c.stage_key === NO_STAGE ? 'text-muted-foreground' : 'font-medium text-slate-800'}>
                            {stageLabel(c.stage_key)}
                          </p>
                          {c.job_id && c.current_stage && c.stage_order === null && (
                            <p className="text-xs text-amber-700">Not in this job's pipeline</p>
                          )}
                        </TableCell>
                        <TableCell
                          className="text-sm whitespace-nowrap tabular-nums"
                          title={c.stage_entered_at ? `Since ${formatDate(c.stage_entered_at, 'dd MMM yyyy')}` : undefined}
                        >
                          {daysLabel(c.days_in_stage) ?? <Dash />}
                        </TableCell>
                        <TableCell>
                          {c.status ? (
                            <Badge className={`${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-700'} font-normal whitespace-nowrap`}>
                              {c.status}
                            </Badge>
                          ) : <Dash />}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap tabular-nums">
                          {isEmpty(c.years_of_experience) ? <Dash /> : `${c.years_of_experience} yrs`}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap tabular-nums">
                          {isEmpty(c.current_ctc) && isEmpty(c.expected_ctc) ? <Dash /> : (
                            <>
                              <p>{lpa(c.current_ctc) ?? '—'} <span className="text-xs text-muted-foreground">current</span></p>
                              <p>{lpa(c.expected_ctc) ?? '—'} <span className="text-xs text-muted-foreground">expected</span></p>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{c.notice_period || <Dash />}</TableCell>
                        <TableCell className="text-sm">
                          {c.source || (c.referred_by ? null : <Dash />)}
                          {c.referred_by && <p className="text-xs text-muted-foreground">Referred by {c.referred_by}</p>}
                        </TableCell>
                        <TableCell>
                          <InterviewList interviews={c.interviews} />
                        </TableCell>
                        {hasOffers && (
                          <TableCell className="text-sm">
                            {c.offer ? (
                              <>
                                <Badge className={`${OFFER_COLORS[c.offer.status] || 'bg-slate-100 text-slate-700'} font-normal`}>
                                  {c.offer.status}
                                </Badge>
                                {c.offer.joining_date && (
                                  <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                                    Joining {formatDate(c.offer.joining_date)}
                                  </p>
                                )}
                              </>
                            ) : <Dash />}
                          </TableCell>
                        )}
                        <TableCell className="text-sm whitespace-nowrap">{formatDate(c.created_at) || <Dash />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {sorted.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
                  <p className="text-sm text-muted-foreground" data-testid="candidate-report-range">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
                  </p>
                  {pageCount > 1 && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1} data-testid="candidate-report-prev">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                      </Button>
                      <span className="text-sm text-muted-foreground tabular-nums">Page {page} of {pageCount}</span>
                      <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= pageCount} data-testid="candidate-report-next">
                        Next <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CandidateReport;
